import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";
import RadioInput from "@builder/components/general/RadioInput";
import SliderInput from "@builder/components/general/SliderInput";
import { getValueUnit } from "@builder/components/general/editUtils";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateChildrenSize, updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { useTranslation } from "react-i18next";

const Layout = ({ parent, path, title, excluded, stateMode, noWrapper }: EditableProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode);
    const { t } = useTranslation();
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const cvModeIndex = cvMode?.id === node?.id ? cvMode.index : null;

    if (!node) return null;

    const style = getStyle(node, path, stateMode, cvModeIndex);
    const stylePath = getStylePath(path, stateMode, cvModeIndex);

    //Flex direction
    let flexDirectionElem = null;
    const flexDirection = style.stateStyle?.flexDirection || style.default?.flexDirection || "column";
    if (!excluded?.includes("flexDirection")) {
        const flexDirectionOptions = [
            { value: "row", img: "/arrow-right-long.svg", checked: "row" === flexDirection, tooltip: t("generalComponents.radioInput.Horizontal") },
            { value: "column", img: "/arrow-down-long.svg", checked: "column" === flexDirection, tooltip: t("generalComponents.radioInput.Vertical") }
        ];
        const flexDirectionCallback = (value: string) => {
            const currentJustifyContent = style.stateStyle?.justifyContent || style.default?.justifyContent || "flex-start";
            const currentAlignItems = style.stateStyle?.alignItems || style.default?.alignItems || "flex-start";
            
            dispatch(
                updateNodeProperty({
                    propertyPath: stylePath,
                    value: {
                        flexDirection: value,
                        // swap justify-content and align-items in one call to keep the same visual positioning (up stays up, left stays left, etc.)
                        justifyContent: currentAlignItems,
                        alignItems: currentJustifyContent
                    }
                })
            );
            
            dispatch(updateChildrenSize(value as "column" | "row"));
        };
        flexDirectionElem = (
            <RadioInput
                title="Content direction"
                groupName="layout-content-direction"
                options={flexDirectionOptions}
                callback={flexDirectionCallback}
            />
        );
    }

    //Justify content
    let justifyContentElem = null;
    if (!excluded?.includes("justifyContent")) {
        const justifyContent = style.stateStyle?.justifyContent || style.default?.justifyContent || "flex-start";

        const justifyContentOptions =
            flexDirection === "column"
                ? [
                      {
                          value: "flex-start",
                          img: "./v-align-start.svg",
                          checked: "flex-start" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Top")
                      },
                      {
                          value: "center",
                          img: "./v-align-center.svg",
                          checked: "center" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Center")
                      },
                      {
                          value: "flex-end",
                          img: "./v-align-end.svg",
                          checked: "flex-end" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Bottom")
                      },
                      {
                          value: "space-between",
                          img: "./v-align-apart.svg",
                          checked: "space-between" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Apart")
                      }
                  ]
                : [
                      {
                          value: "flex-start",
                          img: "./align-start.svg",
                          checked: "flex-start" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Left")
                      },
                      {
                          value: "center",
                          img: "./align-center.svg",
                          checked: "center" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Center")
                      },
                      {
                          value: "flex-end",
                          img: "./align-end.svg",
                          checked: "flex-end" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Right")
                      },
                      {
                          value: "space-between",
                          img: "./align-apart.svg",
                          checked: "space-between" === justifyContent,
                          tooltip: t("generalComponents.radioInput.Apart")
                      }
                  ];
        const justifyContentCallback = (value: string) => {
            dispatch(
                updateNodeProperty({
                    propertyPath: [...stylePath, "justifyContent"],
                    value
                })
            );
        };
        justifyContentElem = (
            <RadioInput
                title={flexDirection === "column" ? "Vertical Align" : "Align"}
                groupName="justify-content-group"
                options={justifyContentOptions}
                callback={justifyContentCallback}
            />
        );
    }

    //align items
    let alignItemsElem = null;
    if (!excluded?.includes("alignItems")) {
        const alignItems = style.stateStyle?.alignItems || style.default?.alignItems || "flex-start";
        const alignItemsOptions =
            flexDirection === "column"
                ? [
                      {
                          value: "flex-start",
                          img: "./align-start.svg",
                          checked: "flex-start" === alignItems,
                          tooltip: t("generalComponents.radioInput.Left")
                      },
                      {
                          value: "center",
                          img: "./align-center.svg",
                          checked: "center" === alignItems,
                          tooltip: t("generalComponents.radioInput.Center")
                      },
                      {
                          value: "flex-end",
                          img: "./align-end.svg",
                          checked: "flex-end" === alignItems,
                          tooltip: t("generalComponents.radioInput.Right")
                      }
                  ]
                : [
                      {
                          value: "flex-start",
                          img: "./v-align-start.svg",
                          checked: "flex-start" === alignItems,
                          tooltip: t("generalComponents.radioInput.Top")
                      },
                      {
                          value: "center",
                          img: "./v-align-center.svg",
                          checked: "center" === alignItems,
                          tooltip: t("generalComponents.radioInput.Center")
                      },
                      {
                          value: "flex-end",
                          img: "./v-align-end.svg",
                          checked: "flex-end" === alignItems,
                          tooltip: t("generalComponents.radioInput.Bottom")
                      }
                  ];
        const alignItemsCallback = (value: string) => {
            dispatch(
                updateNodeProperty({
                    propertyPath: [...stylePath, "alignItems"],
                    value
                })
            );
        };
        alignItemsElem = (
            <RadioInput
                title={flexDirection === "column" ? "Align" : "Vertical Align"}
                groupName="align-items-group"
                options={alignItemsOptions}
                callback={alignItemsCallback}
            />
        );
    }

    //Gap
    let gapElem = null;
    if (!excluded?.includes("gap")) {
        const gapString = style.stateStyle?.gap || (style.default?.gap as string | undefined);
        const gap = getValueUnit(gapString);
        const gapCallback = (value: string, unit: string) => {
            dispatch(
                updateNodeProperty({
                    propertyPath: [...stylePath, "gap"],
                    value: value + unit
                })
            );
        };
        gapElem = <SliderInput title="Gap" {...gap} min={0} max={30} callback={gapCallback} />;
    }

    if (noWrapper)
        return (
            <>
                {flexDirectionElem}
                {flexDirection === "column" ? alignItemsElem : justifyContentElem}
                {flexDirection === "column" ? justifyContentElem : alignItemsElem}
                {gapElem}
            </>
        );

    return (
        <SidebarCollapse title={title || "Layout"} parent={parent}>
            {flexDirectionElem}
            {flexDirection === "column" ? alignItemsElem : justifyContentElem}
            {flexDirection === "column" ? justifyContentElem : alignItemsElem}
            {gapElem}
        </SidebarCollapse>
    );
};

export default Layout;
